<template>
	<div class="balance_level_tab report_theme_container" :data-theme="theme">
		<TabHeader
			:title="title"
			:description="description"
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth">
			<template v-if="counterpartTypes.length" #center-sub-nav>
				<div class="filters_row is-flex is-align-items-center">
					<div class="sub_nav_filters">
						<button type="button"
							class="filter_btn"
							:class="{ 'is-active': isCuentasMode }"
							@click.stop="setCuentasMode">
							Cuentas
						</button>
					</div>

					<MultiSelectDropdown
						:value="selectedCounterpartTypes"
						:options="counterpartTypes"
						placeholder="Tipos de contraparte"
						@input="setCounterpartTypes" />
				</div>
			</template>
		</TabHeader>

		<!-- ================================================================ -->
		<!-- Summary: una sola card de SALDO acumulado al mes de corte (foto   -->
		<!-- de balance). Sin YTD/LTM de flujo — no aplican a un stock. Se     -->
		<!-- pliega vía CollapsibleSection; storageKey global sincroniza el    -->
		<!-- estado entre todos los tabs de finanzas.                          -->
		<!-- ================================================================ -->
		<CollapsibleSection class="mt-1"
			title="Resumen"
			storageKey="financialReports:summary-expanded">
			<template v-if="summaryHint" #hint>{{ summaryHint }}</template>
			<div class="cards_container is-flex">
				<CardVentas v-for="(card, index) in displayCards" :key="index" v-bind="card" class="mr-4" />
			</div>
		</CollapsibleSection>

		<!-- Tabla de saldos acumulados por período -->
		<ExpensesEvolutionTable class="mt-2"
			:title="`Evolución de ${title}`"
			:emptyMessage="emptyMessage"
			:monthHeaders="monthHeaders"
			:visibleRows="visibleRows"
			:totalRow="totalRow"
			:ratioRow="ratioRow"
			:expandedL2Rows="expandedL2Rows"
			:isLoading="isLoading"
			:error="error"
			:selectedPeriod="selectedPeriodRange"
			:periodOptions="periodOptions"
			:filterPeriod="filterPeriod"
			:showAccumulated="true"
			@toggle-l2="toggleL2"
			@change-period="setPeriodRange"
			@retry="fetchData" />
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapActions, mapMutations } from "vuex";
import MultiSelectDropdown from "@/components/generics/MultiSelectDropdown.vue";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import CollapsibleSection from "@/components/financialReports/CollapsibleSection.vue";
import CardVentas from "@/components/financialReports/CardVentas.vue";
import ExpensesEvolutionTable from "@/components/financialReports/ExpensesEvolutionTable.vue";
import { PERIOD_TYPES, FILTER_PERIOD_OPTIONS } from "@/config/reportStructure";
import { formatPercent, formatRangeLabel } from "@/views/financialReports/utils/periodFormatting";
import { emptyCard } from "@/views/financialReports/utils/summaryCards";
import { parseToMoney } from "@/utils/currency";
import { notificate } from "@/utils/message";

// Tab de un nivel-1 de balance (Activos/Pasivos/Patrimonio). Los tres comparten
// esta vista y el store factory (`createBalanceLevelReport`); sólo cambian por
// props: el `namespace` del módulo Vuex, el título/tema y los tipos de contraparte
// disponibles. La tabla muestra SALDOS acumulados (foto de balance), no flujo.
export default {
	name: "BalanceLevelTab",

	components: {
		TabHeader,
		CollapsibleSection,
		MultiSelectDropdown,
		CardVentas,
		ExpensesEvolutionTable,
	},

	props: {
		// Nombre del módulo Vuex de nivel (assetsReport / liabilitiesReport / equityReport).
		namespace: { type: String, required: true },
		title: { type: String, required: true },
		description: { type: String, default: "" },
		theme: { type: String, default: "activos" },
		// Opciones del multiselect de contraparte; vacío → se oculta.
		counterpartTypes: { type: Array, default: () => [] },
	},

	data() {
		return {
			periodOptions: PERIOD_TYPES,
			expandedL2Rows: [],
			filterOptions: FILTER_PERIOD_OPTIONS,
		};
	},

	computed: {
		...mapState("reportsFilters", ["endMonth", "selectedPeriodRange"]),
		...mapState("financialReports", ["filterPeriod"]),

		// El namespace es dinámico (una prop), así que el estado/getters del módulo
		// de nivel se leen directo del store en vez de con mapState/mapGetters.
		selectedCounterpartTypes() { return this.$store.state[this.namespace].selectedCounterpartTypes; },
		monthHeaders() { return this.$store.state[this.namespace].monthHeaders; },
		isLoading() { return this.$store.state[this.namespace].isLoading; },
		error() { return this.$store.state[this.namespace].error; },
		summaryCards() { return this.$store.state[this.namespace].summaryCards; },
		summaryError() { return this.$store.state[this.namespace].summaryError; },
		tableRows() { return this.$store.getters[`${this.namespace}/tableRows`]; },
		totalRow() { return this.$store.getters[`${this.namespace}/totalRow`]; },
		ratioRow() { return this.$store.getters[`${this.namespace}/ratioRow`]; },
		isCuentasMode() { return this.$store.getters[`${this.namespace}/isCuentasMode`]; },

		getNow() {
			return moment().toDate();
		},

		emptyMessage() {
			return `No hay datos de ${this.title.toLowerCase()} para el período seleccionado.`;
		},

		// Hint compacto al lado del label "Resumen" cuando la sección está plegada
		// — preserva el saldo actual para no perder contexto.
		summaryHint() {
			const card = this.summaryCards && this.summaryCards.current_month;
			return card && card.amount != null ? parseToMoney(card.amount) : "";
		},

		visibleRows() {
			return this.tableRows.filter(row => {
				if (row.level === 2) return true;
				return this.expandedL2Rows.includes(row.parent);
			});
		},

		// Una sola card: saldo acumulado al mes de corte con su variación MoM/YoY.
		displayCards() {
			const card = this.summaryCards && this.summaryCards.current_month;
			if (!card) return [emptyCard()];
			return [this.buildCard(card)];
		},

		fetchSignature() {
			return [
				this.endMonth,
				this.selectedPeriodRange,
				this.selectedCounterpartTypes.join(","),
				this.filterPeriod,
			].join("|");
		},

		summarySignature() {
			return moment(this.endMonth).format("YYYY-MM");
		},
	},

	methods: {
		...mapActions("reportsFilters", ["setPeriodRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),

		fetchData() {
			this.$store.dispatch(`${this.namespace}/fetchData`);
		},
		fetchSummary() {
			this.$store.dispatch(`${this.namespace}/fetchSummary`);
		},
		setCounterpartTypes(types) {
			this.$store.commit(`${this.namespace}/SET_SELECTED_COUNTERPART_TYPES`, types);
		},
		setCuentasMode() {
			this.setCounterpartTypes([]);
		},
		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
		toggleL2(label) {
			const index = this.expandedL2Rows.indexOf(label);
			if (index > -1) {
				this.expandedL2Rows.splice(index, 1);
			} else {
				this.expandedL2Rows.push(label);
			}
		},

		// El saldo es un stock: `showMom` para variación vs mes anterior; sin
		// breakdown por L2 (progressItems vacío → la card no muestra barras).
		buildCard(card) {
			return {
				period: formatRangeLabel(card.period_start, card.period_end),
				amount: card.amount,
				mom: formatPercent(card.mom),
				yoy: formatPercent(card.yoy),
				progressItems: [],
			};
		},
	},

	mounted() {
		this.fetchData();
		this.fetchSummary();
	},

	watch: {
		fetchSignature() { this.fetchData(); },
		summarySignature() { this.fetchSummary(); },
		// El fetch del resumen falla en silencio (la card queda vacía). Un toast
		// hace visible el error sin romper el layout.
		summaryError(message) {
			if (message) notificate(message, "is-danger is-light", "is-top-right");
		},
	},
};
</script>

<style scoped lang="scss">
.balance_level_tab {
	position: relative;
	padding-bottom: 40px;
	height: 100%;
}

// Header común vive en TabHeader.vue. Replicamos las classes del slot
// #center-sub-nav (botón Cuentas + MultiSelectDropdown) localmente porque
// TabHeader es scoped y sus estilos no llegan al markup inyectado al slot.

.filters_row {
	gap: 12px;
}

.sub_nav_filters {
	display: inline-flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
	box-sizing: border-box;
	min-height: 34px;
}

.filter_btn {
	display: inline-flex;
	align-items: center;
	padding: 0 16px;
	min-height: 32px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	line-height: 1;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.15s ease;
	box-sizing: border-box;

	&:hover { background: var(--report-surface); }

	&.is-active {
		background: var(--report-primary);
		color: var(--report-bg);
	}
}

.cards_container {
	width: 100%;
}
</style>
